import React, { useState } from "react";
import { useSelector } from "react-redux";
import Button from "app/popleads/components/ui-kit/Button";
import { canAskAssistant } from "./assistantAccess";
import { askAboutError, canAskAboutError, claimRecentFailure, errorContext } from "./assistantErrorCapture";
import { notAsked } from "./assistantErrorToast";

/* The error is on screen milliseconds after its request failed — a card renders its error state
   from the request's own catch block, and util.handleErrorResponse redirects from there too. Ten
   seconds leaves room for a slow render or route change. */
const MOUNT_WINDOW_MS = 10000;

/**
 * "Ask the assistant" under an error the user is looking at that is not a toast: a card's
 * "Oops something went wrong!" (app/partials/Error) and the 404 / 500 pages. For holders of
 * CRM_ASSISTANT only.
 *
 * The failure behind it is taken when it mounts. React 16 renders a state change made in a promise
 * callback before returning, so a card's error state mounts before the next response is handled,
 * and the newest unclaimed failure at that moment is the card's own — even when several cards on
 * one screen fail together. A card with nothing left to take shares the screen's latest failure:
 * several cards can fail for one request, an account page whose account did not load. Nothing
 * behind it at all, and it still asks, with the screen only.
 *
 * Inside the layout the subheader's window answers, in the extension its panel; with no window
 * mounted there is no button. The 404 / 500 pages mount their own — see AssistantErrorPageHelp — and pass
 * `windowMounted`, since theirs registers only after this first renders. `compact` is the card
 * form: small, out of the print export, and not a click on the card, whose own click retries.
 */
const AssistantErrorHelp = ({ shown, compact = false, windowMounted = false }) => {
    const { user } = useSelector((state) => state.auth);
    const [failure] = useState(() => claimRecentFailure(MOUNT_WINDOW_MS, { orLatestOnScreen: compact }));
    if (!canAskAssistant(user) || (!windowMounted && !canAskAboutError())) return null;

    const ask = (event) => {
        if (compact) event.stopPropagation();
        if (!askAboutError(errorContext({ ...failure, shown }))) notAsked();
    };

    return (
        <Button
            className={compact ? "d-print-none mt-3" : undefined}
            variant="primary"
            buttonStyle="option"
            size={compact ? "xsmall" : "medium"}
            onClick={ask}
        >
            Ask the assistant
        </Button>
    );
};

export default AssistantErrorHelp;
